"use client";

/**
 * B-240 step 3 (2026-09-18): the retailer list folded behind one button.
 *
 * The rows arrive as server-rendered children and are NEVER unmounted: the
 * fold only toggles the `hidden` class on the wrapper, so the HTML a crawler
 * receives is the same with the list open or closed (Google indexes only
 * rendered HTML; the Product structured data's offers must stay on the page).
 *
 * Button = the app's existing secondary button (the amber outline used by
 * the Share button) with a vector chevron. No other element on the page
 * changes.
 *
 * "See it" on the Cheaper-elsewhere strip fires `one-card:see` with the
 * shop slug; the fold opens, scrolls to that row and shows the row card's
 * own hover look for a moment (the same classes the card uses on hover).
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { pillClass } from "@/components/comparison/pillClass";

export const SEE_EVENT = "one-card:see";

interface Props {
  count: number;
  checkedCount: number;
  inStockCount: number;
  /**
   * True only while a live search is still running. Passed by
   * ComparisonStream alone; the saved page never passes it, so its sentence
   * is unchanged. 2026-10-01 (owner): a running search read "We checked 0
   * retailers. 0 have it in stock." - nothing had been checked yet. While
   * searching the card says what is happening and never prints a zero.
   */
  searching?: boolean;
  /** The first shop names to show as pills (already display names). */
  names: string[];
  children: React.ReactNode;
}

const BTN =
  "inline-flex flex-shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-xl border bg-transparent px-5 py-3 text-[15px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 disabled:opacity-60 border-[#A16207]/55 text-[#A16207] hover:border-[#A16207] hover:bg-[#A16207]/[0.08] focus-visible:ring-[#A16207]/60 dark:border-[rgba(251,191,36,0.5)] dark:text-[#fbbf24] dark:hover:border-[#fbbf24] dark:hover:bg-[rgba(251,191,36,0.08)] dark:focus-visible:ring-[rgba(251,191,36,0.6)]";

const HOVER_LOOK =
  "[&>article]:-translate-y-0.5 [&>article]:border-accent/40 [&>article]:shadow-[0_0_0_1px_hsl(var(--accent)/0.18),0_8px_24px_rgba(0,0,0,0.3)]";

/**
 * The card's sentence while a search is still running (owner's words,
 * 2026-10-01). Same paragraph, same type, same green as the finished
 * sentence; only the words differ:
 *   no retailer found yet      "Finding the retailers that sell it."
 *   found, none has it yet     "Checking 6 retailers for you."
 *   at least one has it        "Checking 6 retailers. 1 has it in stock so far."
 */
function SearchingSentence({ total, inStock }: { total: number; inStock: number }) {
  const noun = total === 1 ? "retailer" : "retailers";
  return (
    <p className="text-[17px] font-semibold tracking-[-0.015em] text-foreground">
      {total <= 0 ? (
        "Finding the retailers that sell it."
      ) : inStock <= 0 ? (
        <>Checking {total} {noun} for you.</>
      ) : (
        <>
          Checking {total} {noun}.{" "}
          <span className="text-[var(--cmp-good)]">
            {inStock} {inStock === 1 ? "has" : "have"} it in stock so far.
          </span>
        </>
      )}
    </p>
  );
}

export function RetailerListFold({ count, checkedCount, inStockCount, searching = false, names, children }: Props) {
  const [open, setOpen] = useState(false);
  const [lit, setLit] = useState<string | null>(null);
  const timer = useRef<number | null>(null);
  // 2026-09-19 (owner): when the winner is the only retailer there is nothing
  // behind the button, so the button is not shown (297 of 815 active pages on
  // 19 Sep). The sentence stays; the (empty) list wrapper stays in the HTML,
  // hidden, exactly as when the fold is closed.
  const hasList = count > 0;

  const reveal = useCallback((slug: string) => {
    if (count <= 0) return;
    setOpen(true);
    setLit(slug);
    window.setTimeout(() => {
      document.getElementById(`retailer-${slug}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 60);
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setLit(null), 2600);
  }, [count]);

  useEffect(() => {
    const onSee = (e: Event) => {
      const slug = (e as CustomEvent<string>).detail;
      if (typeof slug === "string" && slug) reveal(slug);
    };
    window.addEventListener(SEE_EVENT, onSee);
    return () => {
      window.removeEventListener(SEE_EVENT, onSee);
      if (timer.current) window.clearTimeout(timer.current);
    };
  }, [reveal]);

  // Expose the highlighted slug to the row wrappers through a data attribute
  // on the container; the wrappers read it with a CSS attribute selector.
  return (
    <section className="mt-8" data-one-card-list>
      {/* Option C (owner sign-off 2026-09-19): one bold sentence with the
          in-stock count in the savings green, the first shop names as the
          page's own neutral pills, "+N more", and the amber outline button.
          Design only; same numbers, same behaviour. */}
      <div className="rounded-2xl border border-border bg-secondary px-6 py-5">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            {searching ? (
              <SearchingSentence total={checkedCount > 0 ? checkedCount : count} inStock={inStockCount} />
            ) : (
            <p className="text-[17px] font-semibold tracking-[-0.015em] text-foreground">
              We checked {checkedCount > 0 ? checkedCount : count}{" "}
              {(checkedCount > 0 ? checkedCount : count) === 1 ? "retailer" : "retailers"}.{" "}
              <span className="text-[var(--cmp-good)]">
                {inStockCount} {inStockCount === 1 ? "has" : "have"} it in stock.
              </span>
            </p>
            )}
            {names.length > 0 ? (
              <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
                {names.map((n) => (
                  <span key={n} className={pillClass("neutral")}>
                    {n}
                  </span>
                ))}
                {count - names.length > 0 ? (
                  <span className="px-1 text-[12px] font-medium text-muted-foreground">+{count - names.length} more</span>
                ) : null}
              </div>
            ) : null}
          </div>
          {hasList ? (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-controls="one-card-retailer-list"
            className={BTN}
          >
            {open ? "Hide the list" : "See all retailers"}
            <svg
              aria-hidden="true"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className={open ? "rotate-180 transition-transform duration-200" : "transition-transform duration-200"}
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>
          ) : null}
        </div>
      </div>
      <div id="one-card-retailer-list" className={open && hasList ? "" : "hidden"} data-lit={lit ?? ""}>
        <p className="mb-4 mt-4 text-center text-xs uppercase tracking-wide text-muted-foreground">
          Sorted by True Final Price
        </p>
        {children}
      </div>
      {/* the hover look for the row that "See it" landed on */}
      {lit ? <LitStyle slug={lit} /> : null}
    </section>
  );
}

/** Applies the row card's own hover classes to the landed row for ~2.6s. */
function LitStyle({ slug }: { slug: string }) {
  useEffect(() => {
    const el = document.getElementById(`retailer-${slug}`);
    if (!el) return;
    const classes = HOVER_LOOK.split(" ");
    el.classList.add(...classes);
    return () => el.classList.remove(...classes);
  }, [slug]);
  return null;
}
